import React, { useContext, ReactElement } from "react";

import { createTheme, ThemeProvider } from "@mui/material/styles";
import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";
import { CognitoUser } from "amazon-cognito-identity-js";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";

import { amplifyConfig } from "amplifyConfig";
import { LoginDialog } from "components";
import {
  ApplicantRoutes,
  ApplicantTravelExpenses,
  Applications,
  Routes,
  TravelExpenses,
} from "containers";
import { ErrorContextProvider } from "hooks/error";
import CognitoAuthProvider, {
  AuthContext,
} from "providers/CognitoAuthProvider";
import {
  injectGA,
  GATrackingComponent,
} from "providers/GoogleAnalyticsProvider";

const theme = createTheme({
  typography: {
    // ref: https://mui.com/customization/typography/
    // In Chinese and Japanese the characters are usually larger,
    // so a smaller fontsize may be appropriate.
    fontSize: 12,
  },
});

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  autoSessionTracking: true,
  environment: process.env.REACT_APP_MONTANA_STAGE,
  integrations: [new Integrations.BrowserTracing()],

  tracesSampleRate: 1.0,
});

const chatbotId = "script-import-kao-chatbot";
const chatbotSrc = "https://service.ai-x-supporter.com/kao6/app.bundle.js";
const injectKaoChatbot = (): void => {
  if (document.getElementById(chatbotId)) {
    // eslint-disable-next-line no-console
    console.debug("already chatbot imported");
    return;
  }

  const importScript = document.createElement("script");
  importScript.id = chatbotId;
  importScript.src = chatbotSrc;
  importScript.charset = "UTF-8";
  document.body.appendChild(importScript);
};
const renderContentWithAuth = (
  isInitialized: boolean,
  isAuthenticated: boolean,
  provider: string | undefined,
  signIn: (provider_: string) => void,
  signOut: () => void,
  user?: CognitoUser
) => {
  if (isInitialized && isAuthenticated) {
    injectGA(user);

    if (provider === process.env.REACT_APP_DISPLAY_KAO_CHATBOT_PROVIDER) {
      injectKaoChatbot();
    }

    return (
      <Router>
        <GATrackingComponent />

        <Switch>
          <Route exact path="/">
            <TravelExpenses />
          </Route>
          <Route path="/routes">
            <Routes />
          </Route>
          <Route path="/applications">
            <Applications />
          </Route>
          <Route path="/approver/users/:userId/prepared_routes/:id">
            <ApplicantRoutes />
          </Route>
          <Route path="/approver/users/:userId/movement_logs/:id">
            <ApplicantTravelExpenses />
          </Route>
        </Switch>
      </Router>
    );
  }

  if (isInitialized && !provider) {
    // if (isInitialized) {
    return <LoginDialog signIn={signIn} signOut={signOut} />;
  }

  return null;
};

const AppContent: React.FC = () => {
  const { isInitialized, isAuthenticated, provider, signIn, signOut, user } =
    useContext(AuthContext);

  return renderContentWithAuth(
    isInitialized,
    isAuthenticated,
    provider,
    async (provider_: string) => {
      try {
        await signIn(provider_);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("error to sign in ", error);
      }
    },
    async () => {
      try {
        await signOut();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("error to sign out ", error);
      }
    },
    user
  );
};

export function App(): ReactElement | null {
  return (
    <Sentry.ErrorBoundary>
      <ErrorContextProvider>
        <ThemeProvider theme={theme}>
          <CognitoAuthProvider amplifyConfig={amplifyConfig}>
            <AppContent />
          </CognitoAuthProvider>
        </ThemeProvider>
      </ErrorContextProvider>
    </Sentry.ErrorBoundary>
  );
}
